import React, { useEffect, useState } from 'react';
import commonStyles from '../../Point.module.scss';
import styles from './Cashiers.module.scss';
import { useSelector } from 'react-redux';
import { getTranslation } from '../../../../utils/translation';

// COMPONENTS
import BalanceTable from './Tables/BalanceTable';
import ResultTable from './Tables/ResultTable';
import Filters from './Filters/Filters';
import ContentSpinner from '../../../shared/Spinner/Spinner';

const CashiersBalance = () => {
    const { weeklyBalance, loading, cashierPayment } = useSelector(state => state.reports);

    const [totalData, setTotalData] = useState({
        total: 0,
        totalCount: 0,
        agentCommission: 0,
        payment: 0,
        limitIncrease: 0,
        extraDebt: 0,
        debt: 0,
    });

    const [salesTable, setSalesTable] = useState({
        headings: [
            getTranslation('balanceReportSalesColumn'),
            getTranslation('balanceReportTicketCountColumn'),
            getTranslation('balanceReportAgentCommissionColumn'),
            getTranslation('balanceReportTotalSaleColumn'),
        ],
        body: [],
    });

    const [cancellationTable, setCancellationTable] = useState({
        headings: [
            getTranslation('balanceReportCancellationColumn'),
            getTranslation('balanceReportTicketCountColumn'),
            getTranslation('balanceReportAgentCommissionColumn'),
            getTranslation('balanceReportTotalCancellationColumn'),
        ],
        body: [],
    });

    const [paymentTable, setPaymentTable] = useState({
        headings: [
            getTranslation('balanceReportPaymentColumn'),
            getTranslation('balanceReportTicketCountColumn'),
            getTranslation('balanceReportAgentCommissionColumn'),
            getTranslation('balanceReportTotalPaymentColumn'),
        ],
        body: [],
    });

    const [refundTable, setRefundTable] = useState({
        headings: [
            getTranslation('balanceReportRefundColumn'),
            getTranslation('balanceReportTicketCountColumn'),
            getTranslation('balanceReportAgentCommissionColumn'),
            getTranslation('balanceReportTotalRefundColumn'),
        ],
        body: [],
    });

    const [refundCommissionTable, setRefundCommissionTable] = useState({
        headings: [
            getTranslation('balanceReportRefundCommissionColumn'),
            getTranslation('balanceReportTicketCountColumn'),
            getTranslation('balanceReportAgentCommissionColumn'),
            getTranslation('balanceReportTotalRefundCommissionColumn'),
        ],
        body: [],
    });

    const [voucherSaleTable, setVoucherSaleTable] = useState({
        headings: [
            getTranslation('balanceReportVoucherSaleColumn'),
            getTranslation('balanceReportVoucherCountColumn'),
            getTranslation('balanceReportTotalVoucherSaleColumn'),
        ],
        body: [],
    });

    const [voucherCancelsTable, setVoucherCancelsTable] = useState({
        headings: [
            getTranslation('balanceReportVoucherCancelColumn'),
            getTranslation('balanceReportVoucherCountColumn'),
            getTranslation('balanceReportTotalVoucherSaleColumn'),
        ],
        body: [],
    });

    const [voucherBetsTable, setVoucherBetsTable] = useState({
        headings: [
            getTranslation('balanceReportVoucherBetsColumn'),
            getTranslation('balanceReportTicketCountColumn'),
            getTranslation('balanceReportAgentCommissionColumn'),
            getTranslation('balanceReportTotalSaleColumn'),
        ],
        body: [],
    });

    const [onlineVoucherSalesTable, setOnlineVoucherSalesTable] = useState({
        headings: [
            getTranslation('balanceReportOnlineVoucherSalesColumn'),
            getTranslation('balanceReportTicketCountColumn'),
            getTranslation('balanceReportAgentCommissionColumn'),
            getTranslation('balanceReportTotalSaleColumn'),
        ],
        body: [],
    });

    const [onlineVoucherRefundsTable, setOnlineVoucherRefundsTable] = useState({
        headings: [
            getTranslation('balanceReportOnlineVoucherRefundsColumn'),
            getTranslation('balanceReportAgentCommissionColumn'),
            getTranslation('balanceReportTotalSaleColumn'),
        ],
        body: [],
    });

    useEffect(() => {
        if (!weeklyBalance) return;
        if (!cashierPayment) return;

        const {
            sales,
            cancellation,
            payment,
            refund,
            refundCommission,
            voucherSales,
            onlineVoucherSales,
            onlineVoucherRefund,
            total,
        } = weeklyBalance;
        const { balanceIncrease, extraDebtCredit, credit } = cashierPayment;

        setSalesTable(prev => {
            return {
                headings: prev.headings,
                body: [
                    [
                        getTranslation('balanceReportSportsBettingPrematch'),
                        sales.preMatchCount,
                        sales.preMatchAgentCommission,
                        sales.preMatchTotal,
                    ],
                    [
                        getTranslation('balanceReportSportsBettingLive'),
                        sales.liveCount,
                        sales.liveAgentCommission,
                        sales.liveTotal,
                    ],
                    [
                        getTranslation('balanceReportTotalColumn'),
                        sales.totalCount,
                        sales.agentCommissionTotal,
                        sales.total,
                    ],
                ],
            };
        });

        setCancellationTable(prev => {
            return {
                headings: prev.headings,
                body: [
                    [
                        getTranslation('balanceReportSportsBettingPrematch'),
                        cancellation.preMatchCount,
                        cancellation.preMatchAgentCommission,
                        cancellation.preMatchTotal,
                    ],
                    [
                        getTranslation('balanceReportSportsBettingLive'),
                        cancellation.liveCount,
                        cancellation.liveAgentCommission,
                        cancellation.liveTotal,
                    ],
                    [
                        getTranslation('balanceReportTotalColumn'),
                        cancellation.totalCount,
                        cancellation.agentCommissionTotal,
                        cancellation.total,
                    ],
                ],
            };
        });

        setPaymentTable(prev => {
            return {
                headings: prev.headings,
                body: [
                    [
                        getTranslation('balanceReportSportsBettingPrematch'),
                        payment.preMatchCount,
                        payment.preMatchAgentCommission,
                        payment.preMatchTotal,
                    ],
                    [
                        getTranslation('balanceReportSportsBettingLive'),
                        payment.liveCount,
                        payment.liveAgentCommission,
                        payment.liveTotal,
                    ],
                    [
                        getTranslation('balanceReportTotalPaymentColumn'),
                        payment.totalCount,
                        payment.agentCommissionTotal,
                        payment.total,
                    ],
                ],
            };
        });

        setRefundTable(prev => {
            return {
                headings: prev.headings,
                body: [
                    [
                        getTranslation('balanceReportSportsBettingPrematch'),
                        refund.preMatchCount,
                        refund.preMatchAgentCommission,
                        refund.preMatchTotal,
                    ],
                    [
                        getTranslation('balanceReportSportsBettingLive'),
                        refund.liveCount,
                        refund.liveAgentCommission,
                        refund.liveTotal,
                    ],
                    [
                        getTranslation('balanceReportTotalRefundColumn'),
                        refund.totalCount,
                        refund.agentCommissionTotal,
                        refund.total,
                    ],
                ],
            };
        });

        setRefundCommissionTable(prev => {
            return {
                headings: prev.headings,
                body: [
                    [
                        getTranslation('balanceReportSportsBettingPrematch'),
                        refundCommission.preMatchCount,
                        refundCommission.preMatchAgentCommission,
                        refund.preMatchTotal,
                    ],
                    [
                        getTranslation('balanceReportSportsBettingLive'),
                        refundCommission.liveCount,
                        refundCommission.liveAgentCommission,
                        refundCommission.liveTotal,
                    ],
                    [
                        getTranslation('balanceReportTotalColumn'),
                        refundCommission.totalCount,
                        refundCommission.agentCommissionTotal,
                        refundCommission.total,
                    ],
                ],
            };
        });

        setVoucherSaleTable(prev => {
            return {
                headings: prev.headings,
                body: [
                    [
                        getTranslation('balanceReportTotalColumn'),
                        voucherSales.allVoucherCount,
                        voucherSales.allVoucherTotal,
                    ],
                ],
            };
        });

        setVoucherCancelsTable(prev => {
            return {
                headings: prev.headings,
                body: [
                    [
                        getTranslation('balanceReportTotalColumn'),
                        voucherSales.cancelledVoucherCount,
                        voucherSales.cancelledVoucherAmount,
                    ],
                ],
            };
        });

        setVoucherBetsTable(prev => {
            return {
                headings: prev.headings,
                body: [
                    [
                        getTranslation('balanceReportSportsBettingPrematch'),
                        voucherSales.preMatchCount,
                        voucherSales.preMatchAgentCommission,
                        voucherSales.preMatchTotal,
                    ],
                    [
                        getTranslation('balanceReportSportsBettingLive'),
                        voucherSales.liveCount,
                        voucherSales.liveAgentCommission,
                        voucherSales.liveTotal,
                    ],
                    [
                        getTranslation('balanceReportTotalColumn'),
                        voucherSales.totalCount,
                        voucherSales.agentCommissionTotal,
                        voucherSales.total,
                    ],
                ],
            };
        });

        setOnlineVoucherSalesTable(prev => {
            return {
                headings: prev.headings,
                body: [
                    [
                        getTranslation('balanceReportSportsBettingPrematch'),
                        onlineVoucherSales.preMatchCount,
                        onlineVoucherSales.preMatchAgentCommission,
                        onlineVoucherSales.preMatchTotal,
                    ],
                    [
                        getTranslation('balanceReportSportsBettingLive'),
                        onlineVoucherSales.liveCount,
                        onlineVoucherSales.liveAgentCommission,
                        onlineVoucherSales.liveTotal,
                    ],
                    [
                        getTranslation('balanceReportTotalColumn'),
                        onlineVoucherSales.totalCount,
                        onlineVoucherSales.agentCommissionTotal,
                        onlineVoucherSales.total,
                    ],
                ],
            };
        });

        setOnlineVoucherRefundsTable(prev => {
            return {
                headings: prev.headings,
                body: [
                    [
                        getTranslation('balanceReportSportsBettingPrematch'),
                        onlineVoucherRefund.preMatchAgentCommission,
                        onlineVoucherRefund.preMatchTotal,
                    ],
                    [
                        getTranslation('balanceReportSportsBettingLive'),
                        onlineVoucherRefund.liveAgentCommission,
                        onlineVoucherRefund.liveTotal,
                    ],
                    [
                        getTranslation('balanceReportTotalRefundColumn'),
                        onlineVoucherRefund.agentCommissionTotal,
                        onlineVoucherRefund.total,
                    ],
                ],
            };
        });

        // Total table
        setTotalData(totalData => ({
            ...totalData,
            total: total.total,
            agentCommission: total.agentCommission,
            payment: total.payment,
            debt: total.debt,
            limitIncrease: balanceIncrease,
            extraDebt: extraDebtCredit,
            credit,
        }));
    }, [weeklyBalance, cashierPayment]);

    return (
        <>
            <Filters />
            <main className={commonStyles.main}>
                <div className={styles.content}>
                    {loading ? (
                        <ContentSpinner />
                    ) : weeklyBalance && cashierPayment ? (
                        <>
                            <div className={'m-b-12'} style={{ gridArea: 'table1' }}>
                                <BalanceTable data={salesTable} />
                            </div>
                            <div className={'m-b-12'} style={{ gridArea: 'table2' }}>
                                <BalanceTable data={cancellationTable} />
                            </div>
                            <div className={'m-b-12'} style={{ gridArea: 'table3' }}>
                                <BalanceTable data={paymentTable} />
                            </div>
                            <div className={'m-b-12'} style={{ gridArea: 'table6' }}>
                                <BalanceTable data={voucherSaleTable} />
                            </div>
                            <div className={'m-b-12'} style={{ gridArea: 'table9' }}>
                                <BalanceTable data={voucherCancelsTable} />
                            </div>
                            <div className={'m-b-12'} style={{ gridArea: 'table7' }}>
                                <BalanceTable data={voucherBetsTable} />
                            </div>
                            {/*<div className={'m-b-12'} style={{ gridArea: 'table7' }}>*/}
                            {/*    <BalanceTable data={voucherRefundTable} />*/}
                            {/*</div>*/}
                            <div className={'m-b-12'} style={{ gridArea: 'table4' }}>
                                <BalanceTable data={refundTable} />
                            </div>
                            <div className={'m-b-12'} style={{ gridArea: 'table5' }}>
                                <BalanceTable data={refundCommissionTable} />
                            </div>
                            <div className={'m-b-12'} style={{ gridArea: 'table10' }}>
                                <BalanceTable data={onlineVoucherSalesTable} />
                            </div>
                            <div className={'m-b-12'} style={{ gridArea: 'table11' }}>
                                <BalanceTable data={onlineVoucherRefundsTable} />
                            </div>
                            <div className={'m-b-12'} style={{ gridArea: 'table8' }}>
                                <ResultTable
                                    total={totalData.total}
                                    totalAgentCommission={totalData.agentCommission}
                                    totalPayment={totalData.payment}
                                    limitIncrease={totalData.limitIncrease}
                                    extraDebt={totalData.extraDebt}
                                    debt={totalData.debt}
                                    credit={totalData.credit}
                                />
                            </div>
                        </>
                    ) : null}
                </div>
            </main>
        </>
    );
};

export default CashiersBalance;
